import React from 'react';
import { flexRender } from '@tanstack/react-table';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faFilter } from '@fortawesome/free-solid-svg-icons';
import Filter from './Filter';

/**
 * Sortable table header component with inline sorting
 * @param {object} header - TanStack header instance
 * @param {object} table - TanStack table instance
 * @param {function} handleAutoResize - Function to handle auto-resize on double-click
 * @param {array} stickyColumns - Array of column IDs that should be sticky
 */
const SortableHeader = ({ header, table, handleAutoResize, stickyColumns = ['select'] }) => {
  const { column } = header;
  const isSelectColumn = column.id === 'select';
  const isStickyColumn = stickyColumns.includes(column.id);
  const canSort = column.getCanSort();
  const sortState = column.getIsSorted();
  const filterVariant = column.columnDef.meta?.filterVariant;
  const isFiltered = column.getIsFiltered();

  let className = 'sticky-header';
  if (isSelectColumn) {
    className += ' sticky-col-checkbox';
  } else if (column.id === 'id' && stickyColumns.includes('id')) {
    className += ' sticky-col-id';
  }

  const style = {
    width: `${column.getSize()}px`,
    minWidth: `${column.getSize()}px`,
    maxWidth: `${column.getSize()}px`,
    cursor: canSort && !isSelectColumn ? 'pointer' : 'default'
  };

  const handleHeaderClick = (e) => {
    if (!canSort || isSelectColumn) return;
    
    // Prevent conflicts with resizer
    if (e.target.closest('.resizer')) return;
    
    // Toggle sort: not sorted -> asc -> desc -> not sorted
    if (!sortState) {
      column.toggleSorting(false); // asc
    } else if (sortState === 'asc') {
      column.toggleSorting(true); // desc
    } else {
      column.clearSorting(); // clear
    }
  };

  // Sort icon
  const iconBtnStyle = (active) => ({
    width: '26px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '6px', fontSize: '0.95rem', cursor: 'pointer',
    border: `1px solid ${active ? '#0d6efd' : '#ced4da'}`,
    backgroundColor: active ? '#e7f1ff' : '#fff',
    color: active ? '#0d6efd' : '#6c757d'
  });

  const getSortIcon = () => {
    if (!canSort || isSelectColumn) return null;
    const icon = sortState === 'asc' ? faSortUp : sortState === 'desc' ? faSortDown : faSort;
    return (
      <span style={iconBtnStyle(!!sortState)} title="Ordina">
        <FontAwesomeIcon icon={icon} />
      </span>
    );
  };

  // Popover filtro stile "click sull'intestazione": solo per le colonne che dichiarano
  // meta.filterVariant, additivo rispetto al sort esistente (icone separate, stopPropagation
  // per non far scattare anche il toggle di ordinamento).
  const filterIcon = filterVariant && (
    <OverlayTrigger
      trigger="click"
      placement="bottom"
      rootClose
      overlay={
        <Popover id={`filter-popover-${column.id}`} onClick={e => e.stopPropagation()}>
          <Popover.Body className="p-0">
            <Filter column={column} />
          </Popover.Body>
        </Popover>
      }
    >
      <span
        style={iconBtnStyle(isFiltered)}
        onClick={e => e.stopPropagation()}
        title="Filtra"
      >
        <FontAwesomeIcon icon={faFilter} />
      </span>
    </OverlayTrigger>
  );

  return (
    <th className={className} style={style} onClick={handleHeaderClick}>
      {isSelectColumn ? (
        flexRender(column.columnDef.header, header.getContext())
      ) : (
        <div 
          className="d-flex flex-column align-items-center justify-content-center px-1 py-1 h-100"
          style={{ width: '100%', overflow: 'hidden' }}
        >
          <span
            className="fw-bold text-center"
            style={{ width: '100%', lineHeight: 1.15, fontSize: '0.8rem', marginBottom: '4px' }}
            title={typeof column.columnDef.header === 'string' ? column.columnDef.header : undefined}
          >
            {flexRender(column.columnDef.header, header.getContext())}
          </span>
          {(canSort || filterIcon) && (
            <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ gap: '4px' }}>
              {getSortIcon()}
              {filterIcon}
            </div>
          )}
        </div>
      )}
      {header.column.getCanResize() && !isSelectColumn && handleAutoResize && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onDoubleClick={() => handleAutoResize(header.column)}
          className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
          style={{ transform: header.column.getIsResizing() ? `translateX(${table.getState().columnSizingInfo.deltaOffset}px)` : '' }}
          title="Doppio click per auto-fit"
        />
      )}
    </th>
  );
};

export default SortableHeader;
